import { Analytics } from '@easy-expense/analytics-client';
import {
  addWorkspaceMember,
  canModifyUsers,
  disableMember,
  useAuthStore,
  useCachedMembers,
  useCachedOrganizations,
  useCurrentWorkspace,
  useCurrentWorkspaceAndOrganizationKeys,
  useSubscriptionStore,
  useWorkspaceKeysStore,
} from '@easy-expense/data-firestore-client';
import type { Organization } from '@easy-expense/data-schema-v2';
import { getOrganizationPath, getWorkspacePath } from '@easy-expense/data-schema-v2';
import { WebEnv } from '@easy-expense/env-mobile';
import { ErrorHandling } from '@easy-expense/error-mobile';
import { Icon } from '@easy-expense/ui-shared-components';
import { Spacer } from '@easy-expense/ui-web-core';
import {
  SUBSCRIPTION_EXPIRY_GRACE_PERIOD_MS,
  wasCreatedBeforeMeteredPaywallCutoff,
} from '@easy-expense/utils-shared';
import Constants from 'expo-constants';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import _ from 'lodash';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { auth } from '../firebase/app';
import { createWorkspace } from '../lib/createWorkspace';
import { setDisablePaywall, useDisablePaywall } from '../lib/disablePaywall';
import type { PoolReconciliation } from '../lib/qaPool';
import {
  MAX_POOL_SIZE,
  clampPoolSize,
  countPoolMembers,
  planPoolReconciliation,
  qaPoolIndex,
} from '../lib/qaPool';

type QaAccount = {
  label: string;
  description: string;
  email: string;
  password: string;
};

const EE_QA_ACCOUNTS: QaAccount[] = [
  {
    label: 'Demo account',
    description: 'Realistic business info for general QA',
    email: 'eeqateam@gmail.com',
    password: 'Easypassword11',
  },
  {
    label: 'Stress account',
    description: 'Large workspace for performance and list UX testing',
    email: 'eeqateam+stress@gmail.com',
    password: 'Easypassword11',
  },
];

/** Sentinel for `workingAccount`: a fresh account has no email until it exists. */
const FRESH_ACCOUNT_KEY = 'fresh-account';
const FRESH_ACCOUNT_PASSWORD = 'Easypassword11';

/**
 * Builds an alias like `eeqateam+20260812-143052@gmail.com`. The local-time stamp
 * is what makes each account unique, and readable enough to tell at a glance which
 * one you just minted.
 */
function freshAccountEmail() {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const time = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `eeqateam+${date}-${time}@gmail.com`;
}

const BG = '#f2f2f2';
const CARD = '#ffffff';
const TEXT = '#262626';
const MUTED = '#737373';
const HAIRLINE = '#e5e5e5';
const PINK = '#e277b6';
const DANGER = '#b91c1c';

const TESTS = [
  { label: 'Test Error', onPress: () => ErrorHandling.report(new Error('Test Error')) },
  {
    label: 'Test Crash',
    onPress: () => {
      throw new Error('Test Crash');
    },
  },
  {
    label: 'Test Analytics',
    onPress: () => Analytics.track('test_event', { testProperty: 'hello world' }),
  },
];

const Card: React.FC<React.PropsWithChildren<{ style?: React.CSSProperties }>> = ({
  children,
  style,
}) => (
  <div
    style={{
      background: CARD,
      borderRadius: 10,
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      // Cards sit in the panel's scrolling flex column, and overflow: hidden drops their
      // automatic min-height to 0 -- without this they shrink to fit and clip their rows
      // instead of letting the column scroll.
      flexShrink: 0,
      ...style,
    }}
  >
    {children}
  </div>
);

const SectionRow: React.FC<
  React.PropsWithChildren<{ onClick?: () => void; onDoubleClick?: () => void; last?: boolean }>
> = ({ children, onClick, onDoubleClick, last }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <div
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px 14px',
        background: hover && onClick ? '#f9f9f9' : CARD,
        cursor: onClick ? 'pointer' : 'default',
        borderBottom: last ? 'none' : `1px solid ${HAIRLINE}`,
        transition: 'background 0.1s',
        gap: 8,
      }}
    >
      {children}
    </div>
  );
};

/**
 * A block of explanatory text that is allowed to wrap -- every other row in the panel is a
 * single ellipsised line, which is fine for a value but hides the middle of a sentence.
 */
const NoteRow: React.FC<React.PropsWithChildren<{ last?: boolean; color?: string }>> = ({
  children,
  last,
  color = MUTED,
}) => (
  <SectionRow last={last}>
    <div style={{ flex: 1, minWidth: 0, fontSize: 11, color, lineHeight: 1.5 }}>{children}</div>
  </SectionRow>
);

const BUBBLE_WIDTH = 236;
/** Enough room for the longest explainer, and only used to decide which side to flip to. */
const BUBBLE_CLEARANCE = 150;

type BubbleAnchor = { left: number; top?: number; bottom?: number };

/**
 * Pins a hover bubble to its trigger in viewport coordinates. The bubble has to be `fixed` rather
 * than absolutely placed inside the section -- every Card clips its overflow, so a bubble in the
 * normal flow would be cut off at the card edge.
 */
function bubbleAnchor(rect: DOMRect): BubbleAnchor {
  const rightAligned = rect.right - BUBBLE_WIDTH;
  const left = Math.max(8, Math.min(rightAligned, window.innerWidth - BUBBLE_WIDTH - 8));
  if (rect.bottom + BUBBLE_CLEARANCE > window.innerHeight) {
    return { left, bottom: window.innerHeight - rect.top + 8 };
  }
  return { left, top: rect.bottom + 8 };
}

/**
 * A section of the panel, collapsed by default. Pass `info` to get an ⓘ beside the title that
 * explains the section on hover -- an explainer pinned permanently above the controls is read
 * once and then only costs space every time after that.
 */
const AccordionSection: React.FC<{
  emoji: string;
  title: string;
  info?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ emoji, title, info, defaultOpen = false, children }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const [anchor, setAnchor] = React.useState<BubbleAnchor | null>(null);

  // The anchor is measured once on enter, so scrolling the panel underneath would strand the
  // bubble away from its icon. Drop it instead and let the next hover re-measure.
  React.useEffect(() => {
    if (!anchor) {
      return;
    }
    const clear = () => setAnchor(null);
    window.addEventListener('scroll', clear, true);
    window.addEventListener('resize', clear);
    return () => {
      window.removeEventListener('scroll', clear, true);
      window.removeEventListener('resize', clear);
    };
  }, [anchor]);

  return (
    <Card>
      <SectionRow onClick={() => setOpen((v) => !v)} last={!open}>
        <span style={{ fontSize: 15, lineHeight: 1 }}>{emoji}</span>
        <span
          style={{ flex: 1, fontSize: 14, fontWeight: 700, color: TEXT, fontFamily: 'sans-serif' }}
        >
          {title}
        </span>
        {info ? (
          <span
            onMouseEnter={(event) =>
              setAnchor(bubbleAnchor(event.currentTarget.getBoundingClientRect()))
            }
            onMouseLeave={() => setAnchor(null)}
            onClick={(event) => event.stopPropagation()}
            style={{ display: 'flex', cursor: 'help' }}
          >
            <Icon name="information-circle-outline" size={16} color={anchor ? PINK : MUTED} />
          </span>
        ) : null}
        <Icon name={open ? 'chevron-up-outline' : 'chevron-down-outline'} size={16} color={MUTED} />
      </SectionRow>
      {open ? <div>{children}</div> : null}
      {anchor ? (
        <div
          style={{
            position: 'fixed',
            left: anchor.left,
            top: anchor.top,
            bottom: anchor.bottom,
            width: BUBBLE_WIDTH,
            padding: '8px 10px',
            borderRadius: 8,
            background: TEXT,
            color: '#ffffff',
            fontSize: 11,
            lineHeight: 1.5,
            fontFamily: 'sans-serif',
            boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
            wordBreak: 'break-word',
            // The bubble sits under the pointer on a flip, so it must never steal the hover.
            pointerEvents: 'none',
            zIndex: 10000,
          }}
        >
          {info}
        </div>
      ) : null}
    </Card>
  );
};

const FAB_SIZE = 44;

function copyToClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(value).catch(() => fallbackCopy(value));
    return;
  }
  fallbackCopy(value);
}

function fallbackCopy(value: string) {
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

/** Builds a Firebase console link that opens the Firestore document at `path` in this project. */
function firestoreConsoleUrl(path: string) {
  return `https://console.firebase.google.com/project/${WebEnv.firebase.projectId}/firestore/databases/-default-/data/~2F${path.replace(/\//g, '~2F')}`;
}

/**
 * Clicking copies the ID. Double-clicking also opens `firestorePath` in the Firebase console,
 * when one is given.
 */
const IdRow: React.FC<{ label: string; value: string; firestorePath?: string; last?: boolean }> = ({
  label,
  value,
  firestorePath,
  last,
}) => {
  const [copied, setCopied] = React.useState(false);

  return (
    <SectionRow
      last={last}
      onClick={() => {
        copyToClipboard(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      onDoubleClick={() => {
        if (firestorePath) {
          window.open(firestoreConsoleUrl(firestorePath), '_blank', 'noopener');
        }
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: MUTED }}>{label}</div>
        <div
          style={{
            fontSize: 12,
            color: TEXT,
            fontFamily: 'monospace',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            userSelect: 'text',
            WebkitUserSelect: 'text',
          }}
        >
          {value || 'none'}
        </div>
      </div>
      <span style={{ fontSize: 11, color: copied ? PINK : MUTED, flexShrink: 0 }}>
        {copied ? 'Copied!' : 'Copy'}
      </span>
    </SectionRow>
  );
};

/** The message sendUserInviteV6 resolves with when the address is already an active org member. */
const ALREADY_A_MEMBER = 'already exists in the organization';

type InviteResponse = { data?: { success?: boolean; message?: string } };

type PoolOperation =
  | { kind: 'invite'; email: string }
  | { kind: 'archive'; email: string; userID: string };

type ReconcileTally = {
  invited: number;
  alreadyPresent: number;
  removed: number;
  failures: string[];
};

function describeError(error: unknown): string {
  return (error as { message?: string })?.message ?? String(error);
}

function summariseTally(tally: ReconcileTally, wasCancelled: boolean): string {
  const parts: string[] = [];
  if (tally.invited) {
    parts.push(`${tally.invited} invited`);
  }
  if (tally.alreadyPresent) {
    parts.push(`${tally.alreadyPresent} already present`);
  }
  if (tally.removed) {
    parts.push(`${tally.removed} removed`);
  }
  if (tally.failures.length) {
    parts.push(`${tally.failures.length} failed`);
  }
  const prefix = wasCancelled ? 'Stopped early — ' : 'Done — ';
  return `${prefix}${parts.join(', ') || 'nothing to do'}`;
}

/**
 * What a Pool Account is called in member lists. Two reasons it is never left out: the raw alias
 * is long and hard to tell apart from the account next to it, and `workspace-sendInvite` throws
 * auth/invalid-display-name -- surfacing as a bare INTERNAL -- when the name is missing and the
 * address has never been invited before.
 */
function poolDisplayName(email: string): string {
  const index = qaPoolIndex(email);
  if (index === null) {
    return email;
  }
  return `QA ${index}`;
}

/** Greys the Reconcile row out while it is running, and again once there is nothing left to do. */
function reconcileColor(isRunning: boolean, hasWork: boolean): string {
  if (isRunning || !hasWork) {
    return MUTED;
  }
  return PINK;
}

/**
 * What pressing Reconcile would do right now, in a sentence -- the count on its own reads as a
 * target, and a target does not tell you whether you are about to add members or drop them.
 */
function describePlan(plan: PoolReconciliation, target: number): string {
  const parts: string[] = [];
  if (plan.toInvite.length) {
    parts.push(`invite ${plan.toInvite.length}`);
  }
  if (plan.toArchive.length) {
    parts.push(`remove ${plan.toArchive.length}`);
  }
  if (parts.length) {
    return `Will ${parts.join(' and ')}, one at a time`;
  }
  if (target === 0) {
    return 'No QA teammates here — nothing to do';
  }
  return `eeqateam +1 to +${target} are already here — nothing to do`;
}

/**
 * Team Generator: reconciles the QA Pool in the current workspace to a chosen Pool Size, in either
 * direction. The section is named for what it does to a workspace -- the QA Pool itself is the
 * shared set of addresses it draws from, and lives in `lib/qaPool`.
 *
 * Invites run one at a time because a Pool Account that has never been used costs a user record, a
 * profile and a real invite email on the backend; every run after that takes the cheap reactivation
 * path instead. Per-call results are advisory only -- the member list listener above is what the
 * count is read from, so an invite that writes the membership and then fails on email still settles
 * correctly.
 */
const TeamGeneratorSection: React.FC = () => {
  const currentUser = useAuthStore.use.currentUser();
  const workspace = useCurrentWorkspace();
  const members = useCachedMembers();
  const currentWorkspacePath = useWorkspaceKeysStore((state) => state.currentWorkspacePath());

  const [poolSize, setPoolSize] = React.useState(10);
  const [progress, setProgress] = React.useState<string | null>(null);
  const [summary, setSummary] = React.useState<string | null>(null);
  const [failures, setFailures] = React.useState<string[]>([]);
  const cancelled = React.useRef(false);

  const self = members.find((member) => member.userID === currentUser?.uid);
  const poolMemberCount = countPoolMembers(members);
  const isRunning = progress !== null;
  const target = clampPoolSize(poolSize);

  const plan = React.useMemo(
    () => planPoolReconciliation(members, poolSize, workspace?.createdBy),
    [members, poolSize, workspace?.createdBy],
  );
  const hasWork = plan.toInvite.length + plan.toArchive.length > 0;

  const applyOperation = async (operation: PoolOperation, tally: ReconcileTally) => {
    if (operation.kind === 'archive') {
      await disableMember(operation.userID);
      tally.removed++;
      return;
    }

    const { data }: InviteResponse = await addWorkspaceMember(
      operation.email,
      currentWorkspacePath,
      poolDisplayName(operation.email),
      'member',
    );

    if (data?.success) {
      tally.invited++;
      return;
    }
    if (data?.message?.includes(ALREADY_A_MEMBER)) {
      tally.alreadyPresent++;
      return;
    }
    tally.failures.push(`${operation.email}: ${data?.message ?? 'unknown error'}`);
  };

  const reconcile = async () => {
    const operations: PoolOperation[] = [
      ...plan.toInvite.map((email) => ({ kind: 'invite', email }) as const),
      ...plan.toArchive.map(
        (member) =>
          ({
            kind: 'archive',
            userID: member.userID,
            email: member.email ?? member.userID,
          }) as const,
      ),
    ];

    if (!operations.length) {
      setSummary(describePlan(plan, target));
      return;
    }

    if (plan.toArchive.length) {
      const removals = plan.toArchive.map((member) => member.email).join('\n');
      const confirmed = window.confirm(
        `Remove ${plan.toArchive.length} QA teammate(s) from this workspace?\n\n${removals}\n\n` +
          'They lose access to this workspace only. The accounts themselves stay, and inviting ' +
          'them again later brings them straight back.',
      );
      if (!confirmed) {
        return;
      }
    }

    cancelled.current = false;
    setSummary(null);
    setFailures([]);
    const tally: ReconcileTally = { invited: 0, alreadyPresent: 0, removed: 0, failures: [] };

    for (const [index, operation] of operations.entries()) {
      if (cancelled.current) {
        break;
      }
      const verb = operation.kind === 'invite' ? 'Inviting' : 'Removing';
      setProgress(`${index + 1} of ${operations.length} · ${verb} ${operation.email}`);
      try {
        await applyOperation(operation, tally);
      } catch (error) {
        tally.failures.push(`${operation.email}: ${describeError(error)}`);
      }
    }

    if (tally.failures.length) {
      console.warn('[Debug] Team Generator reconcile failures', tally.failures);
    }
    setProgress(null);
    setFailures(tally.failures);
    setSummary(summariseTally(tally, cancelled.current));
  };

  if (!self) {
    return null;
  }

  if (!canModifyUsers(self, workspace?.createdBy)) {
    return (
      <AccordionSection emoji="👥" title="Team Generator">
        <NoteRow last>
          Only an admin or the workspace owner can add teammates. Sign in as the account that
          created this workspace to generate a team here.
        </NoteRow>
      </AccordionSection>
    );
  }

  return (
    <AccordionSection
      emoji="👥"
      title="Team Generator"
      info={
        <>
          Fills this workspace with throwaway teammates (up to {MAX_POOL_SIZE}). Pick how many you
          want, press reconcile, and it invites or removes until the workspace matches. Real
          teammates and the workspace owner are never touched.
        </>
      }
    >
      <SectionRow>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>How many QA teammates?</div>
          <div style={{ fontSize: 11, color: MUTED }}>
            {`Right now: ${poolMemberCount} QA of ${members.length} members`}
          </div>
        </div>
        <input
          type="number"
          min={0}
          max={MAX_POOL_SIZE}
          value={poolSize}
          disabled={isRunning}
          onChange={(event) => setPoolSize(clampPoolSize(Number(event.target.value)))}
          style={{
            width: 56,
            padding: '4px 6px',
            fontSize: 13,
            color: TEXT,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 6,
            fontFamily: 'sans-serif',
          }}
        />
      </SectionRow>

      <SectionRow onClick={isRunning || !hasWork ? undefined : reconcile}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: reconcileColor(isRunning, hasWork) }}>
            {isRunning ? 'Working…' : `Reconcile to ${target}`}
          </div>
          <div style={{ fontSize: 11, color: MUTED }}>{describePlan(plan, target)}</div>
        </div>
      </SectionRow>

      <NoteRow last={!failures.length}>
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            {progress ?? summary ?? 'First run is slow.'}
          </span>
          {isRunning ? (
            <span
              onClick={() => (cancelled.current = true)}
              style={{ color: PINK, cursor: 'pointer', flexShrink: 0 }}
            >
              Stop
            </span>
          ) : null}
        </div>
      </NoteRow>

      {failures.map((failure, index) => (
        <NoteRow key={failure} color={DANGER} last={index === failures.length - 1}>
          {failure}
        </NoteRow>
      ))}
    </AccordionSection>
  );
};

const HOUR_MS = 60 * 60 * 1000;

/** "in 2d 5h" / "3h ago" beside a timestamp, so an expiry can be read without date maths. */
function describeRelative(timestamp: number, now: number): string {
  const diff = timestamp - now;
  const hours = Math.round(Math.abs(diff) / HOUR_MS);
  const span = `${Math.floor(hours / 24)}d ${hours % 24}h`;
  if (diff >= 0) {
    return `in ${span}`;
  }
  return `${span} ago`;
}

function formatTimestamp(timestamp: number | null | undefined, now: number): string {
  if (!timestamp) {
    return 'none';
  }
  return `${new Date(timestamp).toLocaleString()} (${describeRelative(timestamp, now)})`;
}

/**
 * Where the current access comes from, in the order the gates weigh it. An org is "in grace" for
 * SUBSCRIPTION_EXPIRY_GRACE_PERIOD_MS after its expiry: both the client store and
 * getWorkspaceSubscriptionStatus keep treating it as subscribed until then.
 */
function describeAccess(params: {
  hasSubscription: boolean;
  activeOrg?: Organization;
  graceOrg?: Organization;
  coverageActive: boolean;
  coveredByTeammate: boolean;
  isLegacyWorkspace: boolean;
}): { text: string; color: string } {
  if (params.activeOrg) {
    return { text: `Own plan, active (${params.activeOrg.name})`, color: TEXT };
  }
  if (params.graceOrg) {
    return { text: `Own plan expired, in 3-day grace (${params.graceOrg.name})`, color: PINK };
  }
  if (params.coverageActive && params.coveredByTeammate) {
    return { text: "Covered by a teammate's plan", color: PINK };
  }
  if (params.coverageActive) {
    return { text: "Covered by this workspace's org plan", color: TEXT };
  }
  if (params.hasSubscription) {
    return { text: 'Stale: flagged subscribed, nothing backs it. Reload.', color: DANGER };
  }
  if (params.isLegacyWorkspace) {
    return { text: 'No plan. Legacy workspace, so never metered', color: PINK };
  }
  return { text: 'No plan', color: MUTED };
}

/** The Disable Paywall switch, with a reminder of what it overrides while it is on. */
const DisablePaywallRows: React.FC = () => {
  const disablePaywall = useDisablePaywall();
  return (
    <>
      <SectionRow onClick={() => setDisablePaywall(!disablePaywall)}>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: TEXT }}>Disable Paywall</span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: 999,
            color: disablePaywall ? '#ffffff' : MUTED,
            background: disablePaywall ? PINK : HAIRLINE,
          }}
        >
          {disablePaywall ? 'On' : 'Off'}
        </span>
      </SectionRow>
      {disablePaywall ? (
        <NoteRow color={PINK}>
          On: every plan-limited action goes through; nothing below is consulted.
        </NoteRow>
      ) : null}
    </>
  );
};

const InfoRow: React.FC<{ label: string; value: string; color?: string; last?: boolean }> = ({
  label,
  value,
  color = TEXT,
  last,
}) => (
  <SectionRow last={last}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: MUTED }}>{label}</div>
      <div
        style={{
          fontSize: 12,
          color,
          fontFamily: 'monospace',
          wordBreak: 'break-word',
          whiteSpace: 'pre-line',
          userSelect: 'text',
          WebkitUserSelect: 'text',
        }}
      >
        {value}
      </div>
    </div>
  </SectionRow>
);

/**
 * Shows why the paywall gates think this user is, or isn't, subscribed: the store's derived
 * flags, the own-organization plan behind them (with its grace window), the workspace coverage
 * check, and the legacy-workspace exemption from the monthly limits.
 */
const SubscriptionInfoSection: React.FC = () => {
  const subscription = useSubscriptionStore();
  const organizations = useCachedOrganizations();
  const workspace = useCurrentWorkspace();
  const currentUserID = useAuthStore.use.currentUser()?.uid;
  const [copied, setCopied] = React.useState(false);
  const disablePaywall = useDisablePaywall();

  const now = Date.now();
  const orgsWithPlan = organizations.filter((org) => !!org.subscriptionExpiryDate);
  const activeOrg = orgsWithPlan.find((org) => (org.subscriptionExpiryDate ?? 0) > now);
  const graceOrg = orgsWithPlan.find(
    (org) => (org.subscriptionExpiryDate ?? 0) + SUBSCRIPTION_EXPIRY_GRACE_PERIOD_MS > now,
  );
  const coveredByTeammate =
    !!subscription.coveringUserID && subscription.coveringUserID !== currentUserID;
  const isLegacyWorkspace = wasCreatedBeforeMeteredPaywallCutoff(workspace);
  const access = describeAccess({
    hasSubscription: subscription.hasSubscription,
    activeOrg,
    graceOrg,
    coverageActive: subscription.coverageActive,
    coveredByTeammate,
    isLegacyWorkspace,
  });

  let seats = 'not loaded';
  if (subscription.seatsLoaded) {
    seats = `${subscription.seatsUsed} of ${subscription.seatsAllowed ?? 'unlimited'}`;
  }

  const copySnapshot = () => {
    const snapshot = {
      access: access.text,
      disablePaywall,
      checkedAt: new Date(now).toISOString(),
      store: _.omitBy(subscription, _.isFunction),
      organizations: orgsWithPlan.map((org) => ({
        key: org.key,
        name: org.name,
        subscriptionExpiryDate: org.subscriptionExpiryDate,
        subscriptionTier: org.subscriptionTier,
        subscriptionSource: org.subscriptionSource,
        seats: org.seats,
      })),
      workspace: { key: workspace?.key, createdAt: workspace?.createdAt, isLegacyWorkspace },
    };
    copyToClipboard(JSON.stringify(snapshot, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <AccordionSection
      emoji="💳"
      title="Subscription Info"
      info="What the paywall gates see right now, unless Disable Paywall is on. An org plan keeps access for 3 days after it expires. Reopen the panel to refresh the times."
      defaultOpen={disablePaywall}
    >
      <DisablePaywallRows />
      <InfoRow label="Access" value={access.text} color={access.color} />
      <InfoRow
        label="hasSubscription / hasProPlanSubscription"
        value={`${subscription.hasSubscription} / ${subscription.hasProPlanSubscription}`}
      />
      <NoteRow>Own organizations with a plan</NoteRow>
      {orgsWithPlan.length === 0 ? <InfoRow label="Plans" value="none" /> : null}
      {orgsWithPlan.map((org) => (
        <InfoRow
          key={org.key}
          label={`${org.name} · ${org.subscriptionTier ?? 'no tier'} · ${org.subscriptionSource ?? 'no source'}`}
          value={`expires ${formatTimestamp(org.subscriptionExpiryDate, now)}\ngrace ends ${formatTimestamp(
            (org.subscriptionExpiryDate ?? 0) + SUBSCRIPTION_EXPIRY_GRACE_PERIOD_MS,
            now,
          )}`}
        />
      ))}
      <NoteRow>Workspace coverage check</NoteRow>
      <InfoRow
        label="coverageActive / coverageTier"
        value={`${subscription.coverageActive} / ${subscription.coverageTier ?? 'none'}`}
      />
      <IdRow
        label="Covering user ID"
        value={subscription.coveringUserID ?? ''}
        firestorePath={
          subscription.coveringUserID ? `users/${subscription.coveringUserID}` : undefined
        }
      />
      <IdRow
        label="Subscription ID"
        value={subscription.coverageSubscriptionID ?? ''}
        firestorePath={
          subscription.coverageSubscriptionID
            ? `subscriptions/${subscription.coverageSubscriptionID}`
            : undefined
        }
      />
      <InfoRow label="Checked this session" value={String(subscription.coverageChecked)} />
      <InfoRow label="Seats used" value={seats} />
      <InfoRow
        label="Workspace created"
        value={`${formatTimestamp(workspace?.createdAt, now)}${isLegacyWorkspace ? ' · legacy, never metered' : ''}`}
      />
      <SectionRow last onClick={copySnapshot}>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: TEXT }}>
          Copy snapshot as JSON
        </span>
        <span style={{ fontSize: 11, color: copied ? PINK : MUTED }}>
          {copied ? 'Copied!' : 'Copy'}
        </span>
      </SectionRow>
    </AccordionSection>
  );
};

const DebugPanel: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useAuthStore.use.currentUser();
  const { organizationKey, workspaceKey } = useCurrentWorkspaceAndOrganizationKeys();
  const [showDebug, setShowDebug] = React.useState(false);
  const disablePaywall = useDisablePaywall();
  const [workingAccount, setWorkingAccount] = React.useState<string | null>(null);
  // Unclamped logical position — viewport clamping applied at render time only
  const [position, setPosition] = React.useState({ right: 16, bottom: 80 });
  const [isDragging, setIsDragging] = React.useState(false);

  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    startRight: number;
    startBottom: number;
    moved: boolean;
  } | null>(null);

  const buildInfo = React.useMemo(
    () =>
      JSON.stringify(
        {
          name: Constants.expoConfig?.name ?? 'unknown',
          version: Constants.expoConfig?.version ?? 'unknown',
          sdkVersion: Constants.expoConfig?.sdkVersion ?? 'unknown',
          runtimeVersion: Constants.expoRuntimeVersion ?? 'unknown',
        },
        null,
        2,
      ),
    [],
  );

  // Force re-render on resize so displayRight/displayBottom recompute from current viewport
  const [, setResizeTick] = React.useState(0);
  React.useEffect(() => {
    const onResize = () => setResizeTick((t) => t + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const displayRight = Math.max(0, Math.min(position.right, window.innerWidth - FAB_SIZE));
  const displayBottom = Math.max(0, Math.min(position.bottom, window.innerHeight - FAB_SIZE));

  const handlePointerDown = (e: React.PointerEvent<HTMLElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      // Start from displayed (clamped) position so drag doesn't jump after a resize
      startRight: displayRight,
      startBottom: displayBottom,
      moved: false,
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLElement>) => {
    if (!dragRef.current) {
      return;
    }
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (!dragRef.current.moved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
      dragRef.current.moved = true;
      setIsDragging(true);
    }
    if (dragRef.current.moved) {
      setPosition({
        right: Math.max(0, dragRef.current.startRight - dx),
        bottom: Math.max(0, dragRef.current.startBottom - dy),
      });
    }
  };

  const handlePointerUp = () => {
    const wasDragging = dragRef.current?.moved ?? false;
    dragRef.current = null;
    setIsDragging(false);
    if (!wasDragging) {
      setShowDebug((prev) => !prev);
    }
  };

  const handleQaLogin = async (email: string, password: string) => {
    if (workingAccount) {
      return;
    }
    setWorkingAccount(email);
    try {
      await signOut(auth);
      await signInWithEmailAndPassword(auth, email, password);
      // Full reload resets all Zustand stores to a clean state for the new account.
      // Preserve search and hash so RequireAuth's stored redirect target is fully restored.
      const fromLocation = (
        location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null
      )?.from;
      const from = fromLocation
        ? `${fromLocation.pathname ?? ''}${fromLocation.search ?? ''}${fromLocation.hash ?? ''}`
        : '/transactions';
      window.location.href = from;
    } catch (error) {
      console.warn('[Debug] QA login failed', email, error);
      alert(
        `Login failed for ${email}: ${(error as { message?: string }).message ?? String(error)}`,
      );
    } finally {
      setWorkingAccount(null);
    }
  };

  /**
   * Creates a brand-new user, organization and workspace, then lands in the app --
   * the state you otherwise only reach by clicking through signup and onboarding.
   * Every click mints a new identity, so nothing is shared with a previous run.
   */
  const handleFreshAccount = async () => {
    if (workingAccount) {
      return;
    }
    setWorkingAccount(FRESH_ACCOUNT_KEY);
    const email = freshAccountEmail();

    try {
      await signOut(auth);
      await createUserWithEmailAndPassword(auth, email, FRESH_ACCOUNT_PASSWORD);
    } catch (error) {
      console.warn('[Debug] Fresh account creation failed', email, error);
      alert(
        `Could not create ${email}: ${(error as { message?: string }).message ?? String(error)}`,
      );
      setWorkingAccount(null);
      return;
    }

    try {
      // 'business' with the default name is what every web signup path produces
      // apart from the personal-only branch of /workspace-type.
      await createWorkspace('business', 'Default');
    } catch (error) {
      // createWorkspace alerts on its own before rethrowing. Hand the workspace-less
      // user to the real onboarding page, which creates one itself.
      console.warn('[Debug] Fresh account workspace creation failed', email, error);
      setWorkingAccount(null);
      navigate('/workspace-type');
      return;
    }

    // Full reload resets all Zustand stores for the new account. A fresh workspace
    // can't own the previous account's deep links, so always land on the dashboard.
    window.location.href = '/transactions';
  };

  const isFreshWorking = workingAccount === FRESH_ACCOUNT_KEY;

  return (
    <div
      style={{
        position: 'fixed',
        right: displayRight,
        bottom: displayBottom,
        zIndex: 9999,
        userSelect: 'none',
      }}
    >
      {showDebug ? (
        <div
          style={{
            width: 280,
            background: BG,
            borderRadius: 14,
            boxShadow: '0 12px 36px rgba(0,0,0,0.18)',
            border: `1px solid ${HAIRLINE}`,
            overflow: 'hidden',
            fontFamily: 'sans-serif',
            display: 'flex',
            flexDirection: 'column',
            // The panel is anchored at `bottom`, so its own height has to be capped by
            // whatever room is left above that anchor -- otherwise the top (the header,
            // and the sections nearest it) is clipped off-screen on shorter windows.
            maxHeight: `calc(100vh - ${displayBottom + 16}px)`,
          }}
        >
          <div
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 14px',
              background: CARD,
              borderBottom: `1px solid ${HAIRLINE}`,
              cursor: isDragging ? 'grabbing' : 'grab',
              flexShrink: 0,
            }}
          >
            <Icon name="bug-outline" size={18} color={PINK} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: TEXT, lineHeight: 1.3 }}>
                Debug Menu
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: MUTED,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {currentUser?.email ?? (currentUser?.isAnonymous ? 'Anonymous' : 'Not signed in')}
              </div>
            </div>
            <div
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setShowDebug(false);
              }}
              style={{
                width: 26,
                height: 26,
                borderRadius: 6,
                background: BG,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: MUTED,
                fontSize: 14,
                cursor: 'pointer',
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              ✕
            </div>
          </div>

          <div
            style={{
              padding: 10,
              // Take whatever the header leaves. minHeight: 0 lets a flex child actually
              // shrink below its content size, which is what makes the scroll kick in.
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              // Previously 'none', which hid the fact that content ran past the cap --
              // overflow read as the panel being cut off rather than scrollable.
              scrollbarWidth: 'thin',
            }}
          >
            <AccordionSection emoji="🔐" title="QA accounts" defaultOpen>
              {EE_QA_ACCOUNTS.map((account) => {
                const isOtherWorking = !!workingAccount && workingAccount !== account.email;
                return (
                  <SectionRow
                    key={account.email}
                    onClick={
                      workingAccount
                        ? undefined
                        : () => handleQaLogin(account.email, account.password)
                    }
                  >
                    <div style={{ flex: 1, minWidth: 0, opacity: isOtherWorking ? 0.4 : 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>
                        {workingAccount === account.email ? 'Signing in…' : account.label}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: MUTED,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {account.description}
                      </div>
                    </div>
                  </SectionRow>
                );
              })}
              <SectionRow last onClick={workingAccount ? undefined : handleFreshAccount}>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    opacity: !!workingAccount && !isFreshWorking ? 0.4 : 1,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>
                    {isFreshWorking ? 'Creating account…' : 'Fresh account'}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: MUTED,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    Brand-new user + empty workspace
                  </div>
                </div>
              </SectionRow>
            </AccordionSection>

            <TeamGeneratorSection />

            <SubscriptionInfoSection />

            <AccordionSection emoji="🆔" title="IDs" defaultOpen>
              <IdRow
                label="User ID"
                value={currentUser?.uid ?? ''}
                firestorePath={currentUser ? `users/${currentUser.uid}` : undefined}
              />
              <IdRow
                label="Workspace ID"
                value={workspaceKey}
                firestorePath={
                  workspaceKey ? getWorkspacePath(organizationKey, workspaceKey) : undefined
                }
              />
              <IdRow
                label="Organization ID"
                value={organizationKey}
                firestorePath={organizationKey ? getOrganizationPath(organizationKey) : undefined}
                last
              />
            </AccordionSection>

            <AccordionSection emoji="🧪" title="Tests">
              {TESTS.map(({ label, onPress }, index, arr) => (
                <SectionRow key={label} onClick={onPress} last={index === arr.length - 1}>
                  <span style={{ fontSize: 13, color: TEXT }}>{label}</span>
                </SectionRow>
              ))}
            </AccordionSection>

            <AccordionSection emoji="📋" title="Build Info">
              <div
                style={{
                  padding: '8px 14px',
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                <pre
                  style={{
                    fontSize: 10,
                    color: MUTED,
                    margin: 0,
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                  }}
                >
                  {buildInfo}
                </pre>
              </div>
            </AccordionSection>

            <Spacer.Vertical size={2} />
          </div>
        </div>
      ) : (
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          style={{
            width: FAB_SIZE,
            height: FAB_SIZE,
            borderRadius: '50%',
            background: PINK,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(226,119,182,0.5)',
            cursor: isDragging ? 'grabbing' : 'grab',
            position: 'relative',
          }}
        >
          <Icon name="bug-outline" size={22} color="white" />
          {disablePaywall ? (
            <div
              title="Disable Paywall is on"
              style={{
                position: 'absolute',
                top: -4,
                right: -4,
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: DANGER,
                border: '2px solid white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="lock-open" size={11} color="white" />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

const Debug: React.FC = () => {
  if (!WebEnv.isStaging) {
    return null;
  }
  return <DebugPanel />;
};

export { Debug };
